import { useRef, useState, type ChangeEvent } from 'react';
import {
  Database,
  RefreshCw,
  Plus,
  CheckCircle2,
  XCircle,
  Info,
  Sparkles,
  Upload,
  Loader2,
  AlertTriangle,
  FilePlus2,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  PageHeader,
  Table,
  useToast,
  type TableColumn,
} from '../../ui';
import { useKnowledgeBases } from '../../features/knowledgeBases/useKnowledgeBases';
import { isValidSectionKey, sanitizeSectionKey } from '../../features/knowledgeBases/kbProvisioning';
import { UPLOAD_ACCEPT } from '../../features/knowledgeBases/kbUpload';
import type { TenantKnowledgeBase, KbSummary } from '../../api';

/**
 * Knowledge Bases management — lists the tenant's registered Bedrock KBs and
 * lets the team register its own KB under a logical bundle key. The account's
 * Bedrock KBs are offered as a pick-from list to prefill the register form.
 *
 * The page reaches the backend ONLY through the useKnowledgeBases hook (which
 * wraps api.knowledgeBase). Every action handles its error inline.
 */
export function KnowledgeBasesPage() {
  const kb = useKnowledgeBases();
  const { toast } = useToast();

  // Create-by-upload local form state.
  const [createName, setCreateName] = useState('');
  const [createFiles, setCreateFiles] = useState<File[]>([]);
  const createFileRef = useRef<HTMLInputElement>(null);

  // Add-files-to-existing-KB: one shared hidden input targeted at a bundle key.
  const addFilesRef = useRef<HTMLInputElement>(null);
  const pendingKeyRef = useRef<string | null>(null);

  const onCreateFiles = (e: ChangeEvent<HTMLInputElement>) => {
    setCreateFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
  };

  const onCreate = async () => {
    const ok = await kb.createKnowledgeBase(createName, createFiles);
    if (ok) {
      toast({
        tone: 'success',
        title: 'Knowledge base ready',
        description: `${sanitizeSectionKey(createName)} — ingestion started.`,
      });
      setCreateName('');
      setCreateFiles([]);
    }
  };

  const triggerAddFiles = (bundleKey: string) => {
    pendingKeyRef.current = bundleKey;
    addFilesRef.current?.click();
  };

  const onAddFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    const key = pendingKeyRef.current;
    pendingKeyRef.current = null;
    if (!key || files.length === 0) return;
    const ok = await kb.addFilesToKb(key, files);
    toast(
      ok
        ? { tone: 'success', title: 'Files added', description: `${files.length} file(s) to ${key} — ingestion started.` }
        : { tone: 'error', title: 'Add files failed', description: kb.updateStatus[key] ?? 'Could not add files.' },
    );
  };

  const canCreate = isValidSectionKey(createName) && createFiles.length > 0;

  const tenantColumns: TableColumn<TenantKnowledgeBase>[] = [
    {
      key: 'bundleKey',
      header: 'Bundle key',
      render: (r) => (
        <span className="font-mono text-zinc-200">{r.bundleKey || '—'}</span>
      ),
    },
    {
      key: 'knowledgeBaseId',
      header: 'Knowledge base ID',
      render: (r) => (
        <span className="font-mono text-xs text-zinc-300">
          {r.knowledgeBaseId || '—'}
        </span>
      ),
    },
    {
      key: 'dataSourceId',
      header: 'Data source',
      render: (r) => (
        <span className="font-mono text-xs text-zinc-400">
          {r.dataSourceId || '—'}
        </span>
      ),
    },
    {
      key: 'bucket',
      header: 'Bucket',
      render: (r) => (
        <span className="text-xs text-zinc-400">
          {r.bucket ? (
            <>
              {r.bucket}
              {r.bucketPrefix ? (
                <span className="text-zinc-500">/{r.bucketPrefix}</span>
              ) : null}
            </>
          ) : (
            '—'
          )}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Files',
      render: (r) => {
        const busy = kb.updatingKey === r.bundleKey;
        const status = kb.updateStatus[r.bundleKey];
        return (
          <div className="flex flex-col items-start gap-1">
            <Button
              variant="outline"
              size="sm"
              loading={busy}
              disabled={!!kb.updatingKey && !busy}
              leftIcon={!busy ? <FilePlus2 className="h-3.5 w-3.5" /> : undefined}
              onClick={() => triggerAddFiles(r.bundleKey)}
            >
              Add files
            </Button>
            {status && (
              <span className="max-w-xs break-words text-[11px] text-zinc-500">
                {status}
              </span>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        icon={<Database className="h-5 w-5" />}
        title="Knowledge Bases"
        description="Register and manage your team's Bedrock knowledge bases."
        actions={
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<RefreshCw className="h-4 w-4" />}
            loading={kb.tenantLoading}
            onClick={() => void kb.refreshTenantKbs()}
          >
            Refresh
          </Button>
        }
      />

      {/* Explainer */}
      <Card className="mb-6">
        <CardBody className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-surface-3 text-accent">
            <Info className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="space-y-2 text-sm text-zinc-400">
            <p>
              Registering a knowledge base connects your team's own{' '}
              <span className="text-zinc-200">Bedrock Knowledge Base</span> to
              Vector under a logical{' '}
              <span className="text-zinc-200">bundle key</span>. Requests that
              carry that bundle key are then routed to your knowledge base; the
              default knowledge base is unchanged.
            </p>
            <p>
              Pick one of your account's Bedrock knowledge bases below to
              prefill the form, then supply the data source, bucket, and model
              ARN.
            </p>
          </div>
        </CardBody>
      </Card>

      {/* Registered tenant KBs */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Registered knowledge bases</CardTitle>
        </CardHeader>
        <CardBody>
          {kb.tenantError && (
            <p className="mb-3 text-sm text-red-400" role="alert">
              {kb.tenantError}
            </p>
          )}
          <Table<TenantKnowledgeBase>
            columns={tenantColumns}
            rows={kb.tenantKbs}
            rowKey={(r) => `${r.bundleKey}-${r.knowledgeBaseId}`}
            loading={kb.tenantLoading}
            empty="No knowledge bases registered yet. Register one below."
          />
          {/* Shared hidden input for the per-row "Add files" action. */}
          <input
            ref={addFilesRef}
            type="file"
            multiple
            accept={UPLOAD_ACCEPT}
            className="hidden"
            onChange={(e) => void onAddFiles(e)}
          />
        </CardBody>
      </Card>

      {/* Create a knowledge base by uploading files */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Create a knowledge base from files</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="text-sm text-zinc-400">
            Provision a new knowledge base for a section, upload documents, and
            index them — no Bedrock identifiers required. Accepted files:{' '}
            <span className="font-mono text-zinc-300">.txt .md .json .pdf</span>.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Name / section key"
              placeholder="e.g. Runbooks"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              hint={
                createName.trim()
                  ? `Section key: ${sanitizeSectionKey(createName) || '—'}`
                  : 'Provisioned + routed under this key.'
              }
              disabled={kb.creating}
            />
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-zinc-400">Documents</span>
              <input
                ref={createFileRef}
                type="file"
                multiple
                accept={UPLOAD_ACCEPT}
                className="hidden"
                onChange={onCreateFiles}
              />
              <Button
                variant="outline"
                size="sm"
                disabled={kb.creating}
                leftIcon={<Upload className="h-4 w-4" />}
                onClick={() => createFileRef.current?.click()}
              >
                {createFiles.length > 0
                  ? `${createFiles.length} file(s) selected`
                  : 'Choose files'}
              </Button>
              {createFiles.length > 0 && (
                <p className="truncate text-[11px] text-zinc-500">
                  {createFiles.map((f) => f.name).join(', ')}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              loading={kb.creating}
              disabled={!canCreate}
              onClick={() => void onCreate()}
            >
              Create knowledge base
            </Button>
            {kb.createPhase !== 'idle' && kb.createStatus && (
              <span
                className={
                  kb.createPhase === 'error'
                    ? 'inline-flex items-start gap-1.5 text-sm text-red-400'
                    : 'inline-flex items-start gap-1.5 text-sm text-zinc-400'
                }
                role={kb.createPhase === 'error' ? 'alert' : undefined}
              >
                {kb.creating ? (
                  <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
                ) : kb.createPhase === 'ready' ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                ) : kb.createPhase === 'error' ? (
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                ) : null}
                <span className="break-words">{kb.createStatus}</span>
              </span>
            )}
          </div>
          {kb.createError && (
            <p className="text-sm text-red-400" role="alert">
              {kb.createError}
            </p>
          )}
          <p className="rounded-xl border border-surface-border bg-surface-1/40 px-3 py-2.5 text-xs leading-relaxed text-zinc-500">
            Note: the document upload API is text-only, so{' '}
            <span className="text-zinc-300">.txt / .md / .json</span> upload
            faithfully. PDFs are uploaded best-effort as text — true binary
            fidelity isn&rsquo;t supported by the current upload contract.
          </p>
        </CardBody>
      </Card>

      {/* Register form */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Register a knowledge base</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Bundle key"
              placeholder="e.g. my-team-kb"
              value={kb.form.bundleKey}
              onChange={(e) => kb.setField('bundleKey', e.target.value)}
              hint="Logical key requests are routed under."
            />
            <Input
              label="Knowledge base ID"
              placeholder="e.g. ABCDEF1234"
              value={kb.form.knowledgeBaseId}
              onChange={(e) => kb.setField('knowledgeBaseId', e.target.value)}
            />
            <Input
              label="Data source ID"
              placeholder="e.g. DSRC567890"
              value={kb.form.dataSourceId}
              onChange={(e) => kb.setField('dataSourceId', e.target.value)}
            />
            <Input
              label="Bucket"
              placeholder="e.g. my-team-kb-source"
              value={kb.form.bucket}
              onChange={(e) => kb.setField('bucket', e.target.value)}
            />
            <Input
              label="Bucket prefix (optional)"
              placeholder="e.g. docs/"
              value={kb.form.bucketPrefix}
              onChange={(e) => kb.setField('bucketPrefix', e.target.value)}
              hint="Required object-key prefix within the bucket; may be empty."
            />
            <Input
              label="Model ARN"
              placeholder="arn:aws:bedrock:…:foundation-model/…"
              value={kb.form.modelArn}
              onChange={(e) => kb.setField('modelArn', e.target.value)}
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              loading={kb.registering}
              disabled={!kb.canRegister}
              onClick={() => void kb.register()}
            >
              Register knowledge base
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={kb.registering}
              onClick={kb.resetForm}
            >
              Reset
            </Button>
            {kb.registered === true && (
              <span className="inline-flex items-center gap-1.5 text-sm text-emerald-300">
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                Registered
                <Badge tone="success" className="ml-1">
                  registered=true
                </Badge>
              </span>
            )}
            {kb.registered === false && (
              <span
                className="inline-flex items-center gap-1.5 text-sm text-amber-300"
                role="alert"
              >
                <XCircle className="h-4 w-4" aria-hidden="true" />
                Not registered
                <Badge tone="warning" className="ml-1">
                  registered=false
                </Badge>
              </span>
            )}
          </div>
          {kb.registerError && (
            <p className="text-sm text-red-400" role="alert">
              {kb.registerError}
            </p>
          )}
        </CardBody>
      </Card>

      {/* Account Bedrock KBs (prefill picker) */}
      <Card>
        <CardHeader className="items-center">
          <CardTitle>Account Bedrock knowledge bases</CardTitle>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<RefreshCw className="h-4 w-4" />}
            loading={kb.accountLoading}
            onClick={() => void kb.refreshAccountKbs()}
          >
            Refresh
          </Button>
        </CardHeader>
        <CardBody>
          {kb.accountError ? (
            <p className="text-sm text-red-400" role="alert">
              {kb.accountError}
            </p>
          ) : kb.accountKbs.length === 0 && !kb.accountLoading ? (
            <EmptyState
              icon={<Database className="h-6 w-6" />}
              title="No account knowledge bases"
              description="No Bedrock knowledge bases were found in this account. You can still register one by entering its identifiers above."
            />
          ) : (
            <ul className="space-y-2">
              {kb.accountKbs.map((k) => (
                <AccountKbRow
                  key={k.knowledgeBaseId}
                  kb={k}
                  onUse={() => kb.prefillFromAccount(k)}
                />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

interface AccountKbRowProps {
  kb: KbSummary;
  onUse: () => void;
}

/** One account Bedrock KB with a "Use to prefill" action. */
function AccountKbRow({ kb, onUse }: AccountKbRowProps) {
  return (
    <li className="flex flex-wrap items-center gap-3 rounded-xl border border-surface-border bg-surface-1 px-3 py-2.5 text-sm">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-zinc-200">{kb.name || '—'}</span>
          {kb.status && <Badge tone="neutral">{kb.status}</Badge>}
        </div>
        <div className="mt-0.5 font-mono text-xs text-zinc-500">
          {kb.knowledgeBaseId}
        </div>
        {kb.description && (
          <p className="mt-1 max-w-xl truncate text-xs text-zinc-500">
            {kb.description}
          </p>
        )}
      </div>
      <Button
        variant="outline"
        size="sm"
        className="ml-auto"
        leftIcon={<Sparkles className="h-4 w-4" />}
        onClick={onUse}
      >
        Use to prefill
      </Button>
    </li>
  );
}

export default KnowledgeBasesPage;
